import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { pricingService } from '../apis/pricingService';
import { PRICING_UPDATED_EVENT } from '../utils/pricingDisplay';

const PricingContext = createContext(null);

export function PricingProvider({ children }) {
  const [byKey, setByKey] = useState({});
  const [plans, setPlans] = useState([]);
  const [planGroups, setPlanGroups] = useState([]);
  const [currency, setCurrency] = useState('USD');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await pricingService.getPublicPlans();
      setByKey(data?.byKey || {});
      setPlans(data?.plans || []);
      setPlanGroups(data?.planGroups || []);
      setCurrency(data?.currency || 'USD');
      setError(null);
    } catch (err) {
      setError(err);
      console.error('Failed to load pricing:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onUpdated = () => load();
    window.addEventListener(PRICING_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(PRICING_UPDATED_EVENT, onUpdated);
  }, [load]);

  const getAmount = useCallback(
    (planKey, billingPeriod, seatCount = 1) =>
      pricingService.getAmountWithFallback(byKey, planKey, billingPeriod, seatCount),
    [byKey]
  );

  const getCheckoutAmount = useCallback(
    (planKey, billingPeriod, seatCount = 1) =>
      pricingService.getCheckoutAmountWithFallback(
        byKey,
        planKey,
        billingPeriod,
        seatCount
      ),
    [byKey]
  );

  const getDescription = useCallback(
    (planKey, billingPeriod) =>
      pricingService.getDescription(byKey, planKey, billingPeriod),
    [byKey]
  );

  const getPlanMeta = useCallback(
    (planKey, billingPeriod) =>
      pricingService.getPlanMeta(byKey, planKey, billingPeriod),
    [byKey]
  );

  const value = useMemo(
    () => ({
      byKey,
      plans,
      planGroups,
      currency,
      loading,
      error,
      refresh: load,
      getAmount,
      getCheckoutAmount,
      getDescription,
      getPlanMeta,
    }),
    [
      byKey,
      plans,
      planGroups,
      currency,
      loading,
      error,
      load,
      getAmount,
      getCheckoutAmount,
      getDescription,
      getPlanMeta,
    ]
  );

  return (
    <PricingContext.Provider value={value}>{children}</PricingContext.Provider>
  );
}

export function usePricing() {
  const ctx = useContext(PricingContext);
  if (!ctx) {
    throw new Error('usePricing must be used within PricingProvider');
  }
  return ctx;
}
